import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { crm } from './crmApi';
import type { CrmAuth, MyWeekData } from './crmApi';
import LeadPanel from './LeadPanel';
import { Empty, ShiftChip, Spinner, StageChip, fmtPhone, fmtWhen, goldBg, useCrmMe } from './ui';
import { C, Card, Columns, Ring, Tile, dayLabel, deltaOf, greeting, nf } from './viz';

// ── My Week: the salesperson's home ──────────────────────────
// This week's conversion rate and level, what it takes to reach the next
// one, today's calls, the team this week, and their Not Interested leads to
// try again. Mirrors the app's CrmMyWeekScreen.

const MEDAL = ['🥇', '🥈', '🥉'];

// What it takes to move up the team this week
function rankLine(team: MyWeekData['team'], me: MyWeekData['team'][number]) {
  if (!team.some(t => t.converted > 0)) return 'Nobody has converted yet this week. Be the first!';
  if (me.rank === 1) return 'You lead the team. Stay ahead!';
  const ahead = team[me.rank - 2];
  const n = ahead.converted - me.converted + 1;
  return `${n} more conversion${n === 1 ? '' : 's'} to pass ${ahead.name.split(' ')[0]}`;
}

const MyWeek: React.FC<{ auth: CrmAuth; onNavigate: (page: string, stage?: string) => void }> = ({ auth, onNavigate }) => {
  const { me } = useCrmMe(auth);
  const [d, setD] = useState<MyWeekData | null>(null);
  const [failed, setFailed] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const load = useCallback(async () => {
    try { setD(await crm.myWeek(auth)); setFailed(''); } catch (e: any) { setFailed(e.message); toast.error(e.message); }
  }, [auth]);
  useEffect(() => {
    load();
    const t = setInterval(() => { if (document.visibilityState === 'visible') load(); }, 120000);
    return () => clearInterval(t);
  }, [load]);

  if (!d) return failed ? <Empty icon="⚠️" title="Could not load your week" sub={failed} /> : <Spinner label="Loading your week…" />;
  const w = d.thisWeek, last = d.lastWeek;
  const first = (d.name || '').split(' ')[0];
  const push = !w.base
    ? 'Send your first brochure this week. Every brochure is a step up.'
    : d.next && d.needMore && d.reaches
      ? `${d.needMore} more conversion${d.needMore === 1 ? '' : 's'} from the leads you sent a brochure takes you to ${d.reaches.emoji} ${d.reaches.name}.`
      : 'You are at the top level. Keep it there!';
  const vsLast = deltaOf(w.pct, last.pct, ' pts');
  const work = d.todayWork;
  const topConv = Math.max(1, ...d.team.map(t => t.converted));
  const myRow = d.team.find(t => t.me);
  const shownTeam = d.team.length > 8 ? [...d.team.slice(0, 5), ...(myRow && myRow.rank > 5 ? [myRow] : [])] : d.team;
  const goal = d.goal;
  const goalPct = goal ? Math.min(100, Math.round((goal.admissions / goal.target) * 100)) : 0;

  return (
    <div className="space-y-4">
      {/* Hero: the week's conversion rate and level */}
      <div className="relative overflow-hidden rounded-3xl p-5 sm:p-7 text-white" style={{ background: 'linear-gradient(135deg,#1a1428 0%,#2b2240 45%,#4e3a69 100%)' }}>
        <div className="absolute -top-20 -right-16 w-72 h-72 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(240,180,41,0.28), transparent 70%)' }} />
        <div className="absolute -bottom-24 -left-16 w-72 h-72 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(255,101,53,0.22), transparent 70%)' }} />
        <div className="relative flex flex-col lg:flex-row items-center gap-6">
          <div className="flex-1 min-w-0 text-center lg:text-left">
            <p className="text-white/60 text-sm">{greeting()}, {first} 👋</p>
            <h1 className="text-2xl sm:text-3xl font-bold mt-1">Your week · {dayLabel(d.week.from)} – {dayLabel(d.week.to)}</h1>
            {d.shift && <div className="mt-2 flex flex-wrap gap-2 justify-center lg:justify-start"><ShiftChip shift={d.shift.thisWeek} onNow={d.shift.onNow} dark />{d.shift.nextWeek && <span className="text-[11px] text-white/60 self-center">next week: {d.shift.nextWeek.name}</span>}</div>}
            <p className="text-white/80 text-sm mt-3 max-w-md mx-auto lg:mx-0">{push}</p>
            {/* The level ladder */}
            <ol className="flex flex-wrap justify-center lg:justify-start gap-1.5 mt-4" aria-label="Levels">
              {d.levels.map(l => {
                const reached = w.base > 0 && w.pct >= l.min;
                const current = l.key === d.level.key;
                return (
                  <li key={l.key} className={`rounded-xl px-2.5 py-1.5 text-center border ${current ? 'border-[#F0B429] bg-white/15' : reached ? 'border-white/20 bg-white/10' : 'border-white/10 opacity-50'}`}>
                    <span className="block text-lg leading-none" aria-hidden>{l.emoji}</span>
                    <span className="block text-[10px] font-bold mt-0.5">{l.name}</span>
                    <span className="block text-[9px] text-white/60">{l.min}%+</span>
                  </li>
                );
              })}
            </ol>
          </div>
          <Ring pct={w.pct} color={C.gold} track={C.goldTrack} label={`Weekly conversion rate ${w.pct}%`}>
            <p className="text-5xl font-bold leading-none">{w.pct}%</p>
            <p className="text-[10px] uppercase tracking-widest text-white/60 mt-1.5">conversion rate</p>
            <p className="text-xs text-white/80 mt-0.5">{w.converted} of {w.base} sent a brochure</p>
          </Ring>
          <div className="w-full lg:w-60 rounded-2xl bg-white/10 border border-white/10 p-4 text-center">
            <p className="text-4xl leading-none" aria-hidden>{d.level.emoji}</p>
            <p className="text-lg font-bold mt-1">{d.level.name}</p>
            <p className="text-xs text-white/60">your level this week</p>
            <p className={`text-xs font-bold mt-2 ${vsLast.good === null ? 'text-white/60' : vsLast.good ? 'text-green-300' : 'text-red-300'}`}>{vsLast.text}</p>
            <p className="text-[11px] text-white/50">Last week {last.pct}% · {last.converted} converted</p>
          </div>
        </div>
        {d.quote && <p className="relative text-xs text-white/60 italic mt-5 text-center lg:text-left">“{d.quote.quote}”{d.quote.author ? ` — ${d.quote.author}` : ''}</p>}
      </div>

      {/* Today and the weekly target */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title="Today" sub={`${work.calls} calls · ${work.pitched} brochures · ${work.converted} converted so far`} className="lg:col-span-2"
          right={<button onClick={() => onNavigate('today')} className="px-4 py-2 rounded-xl text-xs font-bold text-white whitespace-nowrap" style={goldBg}>Start calling →</button>}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { n: work.pollsDue ?? 0, label: 'Polls to update', hint: 'due now' },
              { n: work.callNow, label: 'Call now', hint: 'callbacks, retries, visits' },
              { n: work.fresh, label: 'New to call', hint: 'they move on tomorrow' },
              { n: work.payments ?? 0, label: 'Waiting to pay', hint: 'said Interested' },
            ].map(x => (
              <div key={x.label} className="rounded-xl bg-gray-50 px-3 py-3">
                <p className="text-3xl font-bold" style={{ color: C.ink }}>{nf(x.n)}</p>
                <p className="text-xs font-bold text-gray-700">{x.label}</p>
                <p className="text-[10px] text-gray-400">{x.hint}</p>
              </div>
            ))}
          </div>
        </Card>
        {goal ? (
          <Card title={`${goal.emoji} ${goal.headline || 'Weekly target'}`} sub={goal.message || 'Admissions this week'}>
            <p className="text-3xl font-bold" style={{ color: C.ink }}>{goal.admissions}<span className="text-base text-gray-400 font-semibold"> / {goal.target}</span></p>
            <div className="h-3 rounded-full mt-2" style={{ background: C.goldTrack }} role="meter" aria-valuemin={0} aria-valuemax={goal.target} aria-valuenow={goal.admissions} aria-label="Weekly target">
              <div className="h-3 rounded-full transition-all" style={{ width: `${Math.max(goalPct ? 3 : 0, goalPct)}%`, background: C.gold }} />
            </div>
            <p className="text-xs font-bold text-gray-700 mt-2">{goal.admissions >= goal.target ? '🎉 Target done! Every extra one counts.' : `${goal.target - goal.admissions} more admission${goal.target - goal.admissions === 1 ? '' : 's'} to reach it`}</p>
            {goal.reward && <p className="text-[11px] text-gray-500 mt-1">🎁 {goal.reward}</p>}
          </Card>
        ) : (
          <Card title="Your rank this week" sub="By conversions, then conversion rate">
            {myRow ? (
              <>
                <p className="text-4xl font-bold" style={{ color: C.ink }}>{(myRow.converted > 0 && MEDAL[myRow.rank - 1]) || `#${myRow.rank}`}<span className="text-base text-gray-400 font-semibold"> of {d.team.length}</span></p>
                <p className="text-xs text-gray-500 mt-2">{rankLine(d.team, myRow)}</p>
              </>
            ) : <p className="text-sm text-gray-400">You are not taking leads.</p>}
          </Card>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Tile label="Calls this week" value={nf(w.calls)} delta={deltaOf(w.calls, last.calls)} />
        <Tile label="Talked to" value={nf(w.talked)} delta={deltaOf(w.talked, last.talked)} sub="leads" />
        <Tile label="Brochures sent" value={nf(w.pitched)} delta={deltaOf(w.pitched, last.pitched)} sub="leads" />
        <Tile label="Polls sent" value={nf(w.pollsSent ?? 0)} delta={deltaOf(w.pollsSent ?? 0, last.pollsSent ?? 0)} sub="leads" />
        <Tile label="Interested" value={nf(w.interested ?? 0)} delta={deltaOf(w.interested ?? 0, last.interested ?? 0)} sub="said yes to the poll" />
        <Tile label="Converted" value={nf(w.converted)} delta={deltaOf(w.converted, last.converted)} sub="leads that paid" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Closest to paying" sub="Interested, visits and polls to update. Convert these first.">
          {d.closest.length === 0 ? <p className="text-sm text-gray-400">No Interested, visit or poll leads yet. Send more brochures!</p> : (
            <ul className="divide-y divide-gray-100">
              {d.closest.map(l => (
                <li key={l.id}>
                  <button onClick={() => setOpen(l.id)} className="w-full text-left py-2.5 flex items-center gap-3 hover:bg-purple-50/40 rounded-lg px-1">
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-semibold text-gray-800 truncate">{l.name || fmtPhone(l.phone)}</span>
                      <span className="block text-[11px] text-gray-400 truncate">{l.course || fmtPhone(l.phone)}</span>
                    </span>
                    <StageChip stage={l.stage} small />
                    <span className={`text-xs font-bold whitespace-nowrap ${l.nextAt && new Date(l.nextAt).getTime() < Date.now() ? 'text-red-600' : 'text-gray-700'}`}>{fmtWhen(l.nextAt)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card title="Team this week" sub="Conversions, then conversion rate">
          {shownTeam.length === 0 ? <p className="text-sm text-gray-400">Nobody is taking leads yet.</p> : (
            <ol className="space-y-2">
              {shownTeam.map(t => (
                <li key={t.id} className={`rounded-xl px-3 py-2 ${t.me ? 'bg-purple-50 border border-purple-200' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-3">
                    <span className="w-8 text-center text-lg" aria-label={`Rank ${t.rank}`}>{(t.converted > 0 && MEDAL[t.rank - 1]) || <span className="text-xs font-bold text-gray-400">#{t.rank}</span>}</span>
                    <span className="flex-1 min-w-0 text-sm font-semibold text-gray-800 truncate">{t.name}{t.me ? ' (you)' : ''}</span>
                    <span className="text-right">
                      <b className="text-gray-900">{t.converted}</b><span className="text-[11px] text-gray-400"> converted</span>
                      <span className="block text-[10px] text-gray-500">{t.pct}% · {t.pitched} brochures</span>
                    </span>
                  </div>
                  <div className="h-1.5 rounded-r mt-1.5 ml-11" style={{ background: C.track }}>
                    <div className="h-1.5 rounded-r" style={{ width: `${t.converted ? Math.max(3, (t.converted / topConv) * 100) : 0}%`, background: C.leads }} />
                  </div>
                </li>
              ))}
            </ol>
          )}
        </Card>
      </div>

      <Card title="Your last 8 weeks" sub="Weekly conversion rate: converted out of the leads sent a brochure (or converted) that week">
        <Columns data={d.weeks.map(x => ({ key: x.week, label: dayLabel(x.week), value: x.pct, tip: `${x.pct}% · ${x.converted} of ${x.base} sent a brochure` }))}
          fmt={v => `${Math.round(v)}%`} cap={100} ariaLabel="Weekly conversion rate, last 8 weeks" tableHead={['Week of', 'Conversion rate']} />
      </Card>

      <Card title="Not interested — they may come back" sub={`${d.notInterested.total} lead${d.notInterested.total === 1 ? '' : 's'}. People change their minds: call them again when it feels right.`}
        right={d.notInterested.total > 0 ? <button onClick={() => onNavigate('leads', 'Not Interested')} className="text-xs font-bold text-purple-600 whitespace-nowrap">See all →</button> : undefined}>
        {d.notInterested.leads.length === 0 ? <p className="text-sm text-gray-400">No Not Interested leads.</p> : (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {d.notInterested.leads.map(l => (
              <li key={l.id} className="rounded-xl border border-gray-100 px-3 py-2.5 flex items-center gap-3">
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-semibold text-gray-800 truncate">{l.name || fmtPhone(l.phone)}</span>
                  <span className="block text-[11px] text-gray-500 truncate">{l.reason || 'No reason given'}{l.days !== null ? ` · ${l.days === 0 ? 'today' : `${l.days} day${l.days === 1 ? '' : 's'} ago`}` : ''}</span>
                </span>
                <button onClick={() => setOpen(l.id)} className="px-3 py-1.5 rounded-lg text-xs font-bold border border-purple-200 text-purple-700 hover:bg-purple-50 whitespace-nowrap">📞 Call again</button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {open && me && <LeadPanel auth={auth} leadId={open} me={me} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
};

export default MyWeek;
